import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  CircularProgress,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
// Iconsax (2026-09-04, matching the AI Agents pass) -- Calendar/ArrowDown2
// match the Overview tab's own Date Range control exactly now; InfoCircle
// replaces MUI's InfoOutlined for the 4 column-header tooltip triggers.
import { Calendar, ArrowDown2, InfoCircle } from "iconsax-react";
import DateLabel from "../Dashboard/Features/DateLabel";
import ReportFilterDropdown from "./ReportFilterDropdown";
import {
  fetchBroadcastsOverview,
  fetchBroadcastRepliesOverview,
  fetchBroadcastMessageStatusOverview,
  fetchBroadcastFailedMessages,
} from "../../redux/Slice/Reports/BroadcastsReportSlice";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const FILTERS = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "Last 14 Days", value: "last14" },
  { label: "Custom Range", value: "custom" },
];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const getRangeForFilter = (filter) => {
  const now = new Date();
  if (filter === "today") {
    return { start: startOfDay(now), end: endOfDay(now) };
  }
  if (filter === "week") {
    const dayOfWeek = (now.getDay() + 6) % 7; // 0 = Monday
    const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek));
    const end = endOfDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6));
    return { start, end };
  }
  if (filter === "month") {
    const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return { start, end };
  }
  // "last14" and the initial "custom" state both default to the trailing 14 days.
  const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13));
  const end = endOfDay(now);
  return { start, end };
};

const formatDayLabel = (date) => `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;

// Local-date key (not toISOString, which shifts to UTC) matching the "YYYY-MM-DD"
// keys reportController.js's getBroadcastsOverview groups its `days` response by.
const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Builds one chart bucket per day (or, for wide custom ranges, ~30 grouped
// chunks so the chart stays readable), each zeroed (per metricKeys) and tagged
// with the date keys it covers. mergeDailyCounts fills these in from the API
// response. Shared by both the Broadcasts Overview chart (completed/failed)
// and the Template Replies chart (chatReplies/quoteReplies).
const buildEmptyBuckets = (start, end, metricKeys) => {
  if (!start || !end || start > end) return [];
  const totalDays = Math.floor((end - start) / MS_PER_DAY) + 1;
  const zeroedMetrics = () => Object.fromEntries(metricKeys.map((key) => [key, 0]));
  const buckets = [];

  if (totalDays <= 31) {
    for (let i = 0; i < totalDays; i++) {
      const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      buckets.push({ label: formatDayLabel(day), dateKeys: [toDateKey(day)], ...zeroedMetrics() });
    }
    return buckets;
  }

  const bucketCount = 30;
  const bucketDays = Math.ceil(totalDays / bucketCount);
  for (let i = 0; i < totalDays; i += bucketDays) {
    const bucketStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const bucketEndIndex = Math.min(i + bucketDays - 1, totalDays - 1);
    const bucketEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate() + bucketEndIndex);
    const dateKeys = [];
    for (let d = i; d <= bucketEndIndex; d++) {
      dateKeys.push(toDateKey(new Date(start.getFullYear(), start.getMonth(), start.getDate() + d)));
    }
    const label =
      bucketDays === 1 ? formatDayLabel(bucketStart) : `${formatDayLabel(bucketStart)} - ${formatDayLabel(bucketEnd)}`;
    buckets.push({ label, dateKeys, ...zeroedMetrics() });
  }
  return buckets;
};

// Sums each bucket's per-day API counts (days: [{date, ...metricKeys}]) into the
// chart-ready {label, ...metricKeys} shape.
const mergeDailyCounts = (buckets, days, metricKeys) => {
  const byDate = new Map(days.map((d) => [d.date, d]));
  return buckets.map((bucket) => {
    const metrics = Object.fromEntries(metricKeys.map((key) => [key, 0]));
    for (const dateKey of bucket.dateKeys) {
      const match = byDate.get(dateKey);
      if (match) {
        metricKeys.forEach((key) => {
          metrics[key] += match[key] || 0;
        });
      }
    }
    return { label: bucket.label, ...metrics };
  });
};

const formatDateRangeLabel = (start, end) => {
  if (!start || !end) return "Select Date Range";
  const opts = { month: "short", day: "numeric", year: "numeric" };
  return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, opts)}`;
};

// Y-axis ticks step by 2 (matching the target report's 0,2,4,6,8,10,12 scale)
// and extend past 12 whenever real data exceeds it.
const getYAxisTicks = (maxValue) => {
  const topValue = Math.max(12, Math.ceil(maxValue / 2) * 2);
  const ticks = [];
  for (let v = 0; v <= topValue; v += 2) ticks.push(v);
  return ticks;
};

// not yet wired into the header UI; lastUpdatedAt/timeZoneLabel below are computed
// for the same pending display.
// eslint-disable-next-line no-unused-vars
const formatRelativeTime = (date) => {
  if (!date) return "";
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;
  const diffHr = Math.floor(diffMin / 60);
  return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
};

const getTimeZoneLabel = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, "0");
    const mm = String(abs % 60).padStart(2, "0");
    return `${timeZone} (GMT${sign}${hh}:${mm})`;
  } catch {
    return "";
  }
};

// Broadcasts report tab. Wired to reportController.js's getBroadcastsOverview,
// which counts each broadcast once (as Completed, Partial, or Failed) based on
// its delivery time - see resolveCampaignDeliveryTime there for how that's
// resolved. Partial = stored status stays COMPLETED but some recipients
// failed; Failed = the campaign itself never got going (validation/auth/crash).
// Kept distinct so one bad number in a large broadcast doesn't read as "the
// whole campaign failed".
const BroadcastsReport = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const darkGreen = theme.palette.success.dark;
  const darkAmber = theme.palette.warning.dark;
  const darkRed = theme.palette.error.dark;
  // Chat-reply/quote-reply series colors: categorical (identity), not status — deliberately
  // distinct from the Completed/Failed green/red above so replies don't read as "good/bad".
  // Blue/orange pair validated for CVD-safe adjacent contrast in both themes (dataviz skill).
  const chatReplyColor = theme.palette.mode === "dark" ? "#3987e5" : "#2a78d6";
  const quoteReplyColor = theme.palette.mode === "dark" ? "#d95926" : "#eb6834";
  // Message Status line-chart series colors: categorical (identity, not stacked), validated
  // together via the dataviz skill's palette validator (all adjacent-pair CVD/contrast checks
  // pass in both themes). Failed reuses the same red family as the Overview chart's Failed bar.
  const sentColor = theme.palette.mode === "dark" ? "#3987e5" : "#2a78d6";
  const deliveredColor = theme.palette.mode === "dark" ? "#199e70" : "#1baf7a";
  const readColor = theme.palette.mode === "dark" ? "#9085e9" : "#4a3aa7";
  const failedColor = theme.palette.mode === "dark" ? "#e66767" : "#e34948";
  const [, setFilter] = useState("last14");
  const [range, setRange] = useState(() => getRangeForFilter("last14"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDateLabel, setOpenDateLabel] = useState(false);
  // value not yet rendered; setLastUpdatedAt below already tracks it for when the
  // "last updated" label is added.
  // eslint-disable-next-line no-unused-vars
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  // The report page's shared filter bar (broadcast name, sent by, dataset, label) — applies to
  // both charts below. Each holds the ReportFilterDropdown-selected {value, label} options.
  const [selectedBroadcasts, setSelectedBroadcasts] = useState([]);
  const [selectedSentBy, setSelectedSentBy] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  // eslint-disable-next-line no-unused-vars -- pending display, see formatRelativeTime above.
  const timeZoneLabel = useMemo(getTimeZoneLabel, []);

  const filters = useMemo(
    () => ({
      campaignIds: selectedBroadcasts.map((o) => o.value),
      sentBy: selectedSentBy.map((o) => o.value),
      datasetIds: selectedDatasets.map((o) => o.value),
      labels: selectedLabels.map((o) => o.value),
    }),
    [selectedBroadcasts, selectedSentBy, selectedDatasets, selectedLabels]
  );

  const {
    totalCompleted,
    totalPartial,
    totalFailed,
    days,
    loading: apiLoading,
    error: apiError,
    replies: {
      totalChatReplies,
      totalQuoteReplies,
      days: replyDays,
      loading: repliesLoading,
      error: repliesError,
    },
    messageStatus: {
      totalSent,
      totalDelivered,
      totalFailed: totalStatusFailed,
      totalRead,
      days: statusDays,
      loading: statusLoading,
      error: statusError,
    },
    failedMessages: {
      rows: failedRows,
      total: failedTotal,
      loading: failedLoading,
      error: failedError,
    },
  } = useSelector((state) => state.broadcastsReport);

  // Failed Messages table pagination — server-side (see reportController.js's
  // getBroadcastFailedMessages), since a wide date range can have thousands of failed rows.
  // MUI's TablePagination is 0-indexed; the API is 1-indexed (page/limit sent below).
  const [failedPageIndex, setFailedPageIndex] = useState(0);
  const [failedRowsPerPage, setFailedRowsPerPage] = useState(25);

  useEffect(() => {
    if (!range.start || !range.end) return;
    const params = { startDate: range.start.toISOString(), endDate: range.end.toISOString(), filters };
    dispatch(fetchBroadcastsOverview(params))
      .unwrap()
      .then(() => setLastUpdatedAt(new Date()))
      .catch(() => {});
    dispatch(fetchBroadcastRepliesOverview(params)).unwrap().catch(() => {});
    dispatch(fetchBroadcastMessageStatusOverview(params)).unwrap().catch(() => {});
  }, [dispatch, range.start, range.end, filters]);

  // A new date range or filter selection invalidates whatever page the Failed Messages table
  // was on - back to page 1 so pagination stays in sync with the new result set.
  useEffect(() => {
    setFailedPageIndex(0);
  }, [range.start, range.end, filters]);

  useEffect(() => {
    if (!range.start || !range.end) return;
    dispatch(
      fetchBroadcastFailedMessages({
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        filters,
        page: failedPageIndex + 1,
        limit: failedRowsPerPage,
      })
    )
      .unwrap()
      .catch(() => {});
  }, [dispatch, range.start, range.end, filters, failedPageIndex, failedRowsPerPage]);

  const handleFailedPageChange = (_event, newPageIndex) => setFailedPageIndex(newPageIndex);
  const handleFailedRowsPerPageChange = (event) => {
    setFailedRowsPerPage(parseInt(event.target.value, 10));
    setFailedPageIndex(0);
  };

  const emptyBuckets = useMemo(
    () => buildEmptyBuckets(range.start, range.end, ["completed", "partial", "failed"]),
    [range.start, range.end]
  );
  const chartData = useMemo(
    () => mergeDailyCounts(emptyBuckets, days, ["completed", "partial", "failed"]),
    [emptyBuckets, days]
  );
  const totalBroadcasts = totalCompleted + totalPartial + totalFailed;
  const completedPct = totalBroadcasts ? (totalCompleted / totalBroadcasts) * 100 : 0;
  const partialPct = totalBroadcasts ? (totalPartial / totalBroadcasts) * 100 : 0;
  const failedPct = totalBroadcasts ? (totalFailed / totalBroadcasts) * 100 : 0;
  // Ticks are scaled off the tallest single bar (not the period total) so the
  // axis matches what's actually plotted.
  const yAxisTicks = useMemo(
    () => getYAxisTicks(chartData.reduce((max, d) => Math.max(max, d.completed, d.partial, d.failed), 0)),
    [chartData]
  );

  const repliesEmptyBuckets = useMemo(
    () => buildEmptyBuckets(range.start, range.end, ["chatReplies", "quoteReplies"]),
    [range.start, range.end]
  );
  const repliesChartData = useMemo(
    () => mergeDailyCounts(repliesEmptyBuckets, replyDays, ["chatReplies", "quoteReplies"]),
    [repliesEmptyBuckets, replyDays]
  );
  const totalReplies = totalChatReplies; // quoteReplies is a subset of chatReplies, not additive
  const repliesYAxisTicks = useMemo(
    () => getYAxisTicks(repliesChartData.reduce((max, d) => Math.max(max, d.chatReplies, d.quoteReplies), 0)),
    [repliesChartData]
  );

  const statusEmptyBuckets = useMemo(
    () => buildEmptyBuckets(range.start, range.end, ["sent", "delivered", "failed", "read"]),
    [range.start, range.end]
  );
  const statusChartData = useMemo(
    () => mergeDailyCounts(statusEmptyBuckets, statusDays, ["sent", "delivered", "failed", "read"]),
    [statusEmptyBuckets, statusDays]
  );
  const totalStatusEvents = totalSent + totalDelivered + totalStatusFailed + totalRead;
  const statusYAxisTicks = useMemo(
    () =>
      getYAxisTicks(
        statusChartData.reduce((max, d) => Math.max(max, d.sent, d.delivered, d.failed, d.read), 0)
      ),
    [statusChartData]
  );

  const handleDateButtonClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFilterSelect = (value) => {
    handleMenuClose();
    if (value === "custom") {
      setOpenDateLabel(true);
      return;
    }
    setFilter(value);
    setRange(getRangeForFilter(value));
  };

  const handleCustomDateRange = (startIso, endIso) => {
    setRange({ start: startOfDay(new Date(startIso)), end: endOfDay(new Date(endIso)) });
    setFilter("custom");
    setOpenDateLabel(false);
  };

  const handleClearAll = () => {
    setFilter("last14");
    setRange(getRangeForFilter("last14"));
    setSelectedBroadcasts([]);
    setSelectedSentBy([]);
    setSelectedDatasets([]);
    setSelectedLabels([]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Broadcasts
        </Typography>
       
      </Box>

      <Typography variant="body2" color="text.secondary">
        This report helps to gain insights into the broadcasts results. The report only considers
        broadcasts sent within the selected time period based on Delivery Time.
      </Typography>

      {(apiError || repliesError || statusError || failedError) && (
        <Typography variant="body2" color="error.main">
          {apiError || repliesError || statusError || failedError}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleDateButtonClick}
          startIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><Calendar size="100%" color="currentColor" /></Box>}
          endIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
          sx={{ color: "text.primary", borderColor: "divider", textTransform: "none" }}
        >
          {formatDateRangeLabel(range.start, range.end)}
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {FILTERS.map((f) => (
            <MenuItem key={f.value} onClick={() => handleFilterSelect(f.value)}>
              {f.label}
            </MenuItem>
          ))}
        </Menu>

        <ReportFilterDropdown
          field="broadcast"
          label="Broadcast"
          selected={selectedBroadcasts}
          onChange={setSelectedBroadcasts}
        />
        <ReportFilterDropdown
          field="sentBy"
          label="Sent By"
          selected={selectedSentBy}
          onChange={setSelectedSentBy}
        />
        <ReportFilterDropdown
          field="dataset"
          label="Dataset"
          selected={selectedDatasets}
          onChange={setSelectedDatasets}
        />
        <ReportFilterDropdown
          field="label"
          label="Label"
          selected={selectedLabels}
          onChange={setSelectedLabels}
        />

        <Button variant="text" size="small" onClick={handleClearAll} sx={{ textTransform: "none" }}>
          Clear all
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: "12px",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Broadcasts Overview
          </Typography>
          <Tooltip title="Completed, partial, and failed counts for broadcasts delivered within the selected time period. Partial means the broadcast completed sending but some recipients didn't get it; Failed means the broadcast itself never went out.">
            <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}><InfoCircle size="100%" color="currentColor" /></Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Broadcasts Completed
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography variant="h3" fontWeight={700} color="text.primary">
                {totalCompleted}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {completedPct.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Broadcasts Partial
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography variant="h3" fontWeight={700} color="text.primary">
                {totalPartial}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {partialPct.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Broadcasts Failed
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography variant="h3" fontWeight={700} color="text.primary">
                {totalFailed}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {failedPct.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: "success.dark" }} />
            <Typography variant="caption" color="text.secondary">Completed</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: "warning.dark" }} />
            <Typography variant="caption" color="text.secondary">Partial</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: "error.dark" }} />
            <Typography variant="caption" color="text.secondary">Failed</Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative", width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "currentColor" }}
                interval={0}
                padding={{ left: 12, right: 12 }}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                ticks={yAxisTicks}
                tick={{ fontSize: 11, fill: "currentColor" }}
              />
              <Bar dataKey="completed" name="Completed" fill={darkGreen} radius={[4, 4, 0, 0]} />
              <Bar dataKey="partial" name="Partial" fill={darkAmber} radius={[4, 4, 0, 0]} />
              <Bar dataKey="failed" name="Failed" fill={darkRed} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {apiLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            totalBroadcasts === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  pointerEvents: "none",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  The selected filter(s) have no applicable data.
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: "12px",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Template Replies
          </Typography>
          <Tooltip title="Chat Replies counts any inbound message a contact sent after receiving the broadcast. Quote Replies counts only messages sent using WhatsApp's reply-to-message feature on the template itself, and is included within Chat Replies.">
            <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}><InfoCircle size="100%" color="currentColor" /></Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Chat Replies
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalChatReplies}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Quote Replies
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalQuoteReplies}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: chatReplyColor }} />
            <Typography variant="caption" color="text.secondary">Chat Replies</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: quoteReplyColor }} />
            <Typography variant="caption" color="text.secondary">Quote Replies</Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative", width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={repliesChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "currentColor" }}
                interval={0}
                padding={{ left: 12, right: 12 }}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, repliesYAxisTicks[repliesYAxisTicks.length - 1]]}
                ticks={repliesYAxisTicks}
                tick={{ fontSize: 11, fill: "currentColor" }}
              />
              <Bar dataKey="chatReplies" name="Chat Replies" fill={chatReplyColor} radius={[4, 4, 0, 0]} />
              <Bar dataKey="quoteReplies" name="Quote Replies" fill={quoteReplyColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {repliesLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            totalReplies === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  pointerEvents: "none",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  The selected filter(s) have no applicable data.
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: "12px",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Message Status
          </Typography>
          <Tooltip title="Sent, Delivered, Read, and Failed counts for individual broadcast messages, per day within the selected time period.">
            <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}><InfoCircle size="100%" color="currentColor" /></Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Sent
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalSent}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Delivered
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalDelivered}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Read
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalRead}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              Failed
            </Typography>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {totalStatusFailed}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2, borderRadius: "1px", backgroundColor: sentColor }} />
            <Typography variant="caption" color="text.secondary">Sent</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2, borderRadius: "1px", backgroundColor: deliveredColor }} />
            <Typography variant="caption" color="text.secondary">Delivered</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2, borderRadius: "1px", backgroundColor: readColor }} />
            <Typography variant="caption" color="text.secondary">Read</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2, borderRadius: "1px", backgroundColor: failedColor }} />
            <Typography variant="caption" color="text.secondary">Failed</Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative", width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={statusChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.2)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "currentColor" }}
                interval={0}
                padding={{ left: 12, right: 12 }}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, statusYAxisTicks[statusYAxisTicks.length - 1]]}
                ticks={statusYAxisTicks}
                tick={{ fontSize: 11, fill: "currentColor" }}
              />
              {/* Shared tooltip - hovering any point on the X shows every series' count for that day */}
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: theme.palette.text.secondary, marginBottom: 4 }}
                itemStyle={{ padding: 0 }}
              />
              <Line type="monotone" dataKey="sent" name="Sent" stroke={sentColor} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="delivered" name="Delivered" stroke={deliveredColor} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="read" name="Read" stroke={readColor} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="failed" name="Failed" stroke={failedColor} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>

          {statusLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            totalStatusEvents === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  pointerEvents: "none",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  The selected filter(s) have no applicable data.
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: "12px",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Failed Messages
          </Typography>
          <Tooltip title="Every message that failed to send within the selected time period, with the reason WhatsApp (or our own send attempt) reported.">
            <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}><InfoCircle size="100%" color="currentColor" /></Box>
          </Tooltip>
        </Box>

        <TableContainer
          sx={{
            maxHeight: 500,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "3px", height: "3px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (t) => (t.palette.mode === "dark" ? "#1E1E1E" : "#f1f1f1"),
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "2px",
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Phone Number</strong></TableCell>
                <TableCell><strong>Broadcast</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failedRows.map((row, index) => (
                <TableRow key={`${row.contactId || row.phoneNumber}-${row.campaignId}-${index}`} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: "error.main", fontSize: 14 }}>
                        {(row.contactName || row.phoneNumber || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">
                        {row.contactName || row.phoneNumber || "Unknown"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.phoneNumber || "Unknown"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.broadcastName || "Unknown"}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.failureReason ? (
                      <Tooltip title={row.errorMessage || ""}>
                        <Chip label={row.failureReason} size="small" color="error" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {row.errorMessage || "-"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {failedLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          failedRows.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <Typography variant="caption" color="text.secondary">
                No failed messages in the selected filter(s).
              </Typography>
            </Box>
          )
        )}

        <TablePagination
          component="div"
          count={failedTotal}
          page={failedPageIndex}
          onPageChange={handleFailedPageChange}
          rowsPerPage={failedRowsPerPage}
          onRowsPerPageChange={handleFailedRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {openDateLabel && (
        <DateLabel
          startDateProp={range.start}
          endDateProp={range.end}
          onClose={() => setOpenDateLabel(false)}
          onDateChange={handleCustomDateRange}
          variant="reports"
        />
      )}
    </Box>
  );
};

export default BroadcastsReport;
